import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { BdcDc } from "../../../types/bdc.type";
import { BdcBranch } from "../../../types/bdcDashboard.type";
import { branchLabel } from "./format";

export const ALL = "all";

/** The DCs a branch delivers, or every DC when no branch (or an unknown one) is picked. */
export const dcsForBranch = (dcs: BdcDc[], branches: BdcBranch[], companyId: string): BdcDc[] => {
  if (companyId === ALL) return dcs;
  const codes = branches.find((b) => b.company_id === companyId)?.dc_codes ?? [];
  return codes.length ? dcs.filter((dc) => codes.includes(dc.dc_code)) : dcs;
};

export function DateField({
  label,
  value,
  onChange,
}: Readonly<{ label: string; value: string; onChange: (date: string) => void }>) {
  return (
    <DatePicker
      label={label}
      value={dayjs(value)}
      format="DD/MM/YYYY"
      disableFuture
      onChange={(next: Dayjs | null) => {
        if (next && next.isValid()) onChange(next.format("YYYY-MM-DD"));
      }}
      slotProps={{ textField: { size: "small", sx: { width: 170 } } }}
    />
  );
}

/** สาขา: ทุกสาขา + each — drawn only when the caller sees more than one BDC branch. */
export function BranchSelect({
  branches,
  value,
  onChange,
}: Readonly<{ branches: BdcBranch[]; value: string; onChange: (companyId: string) => void }>) {
  if (branches.length <= 1) return null;
  return (
    <TextField
      select
      size="small"
      label="สาขา"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 160 }}
    >
      <MenuItem value={ALL}>ทุกสาขา</MenuItem>
      {branches.map((branch) => (
        <MenuItem key={branch.company_id} value={branch.company_id}>
          {branchLabel(branch, branch.company_id)}
          {branch.branch_name && branch.branch_code ? ` ${branch.branch_name}` : ""}
          {branch.is_test_mode ? " (ทดสอบ)" : ""}
        </MenuItem>
      ))}
    </TextField>
  );
}

export function DcSelect({
  dcs,
  value,
  onChange,
}: Readonly<{ dcs: BdcDc[]; value: string; onChange: (dcCode: string) => void }>) {
  return (
    <TextField
      select
      size="small"
      label="DC"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 180 }}
    >
      <MenuItem value={ALL}>ทุก DC</MenuItem>
      {/* Keep a picked DC selectable even when a branch switch has filtered it out. */}
      {value !== ALL && !dcs.some((dc) => dc.dc_code === value) && (
        <MenuItem value={value}>{value}</MenuItem>
      )}
      {dcs.map((dc) => (
        <MenuItem key={dc.dc_code} value={dc.dc_code}>
          {dc.dc_code} {dc.dc_name}
        </MenuItem>
      ))}
    </TextField>
  );
}
